import { useState, useEffect, useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react'
import { createPortal } from 'react-dom'
import type { CmsCollection, CmsItem } from '../cmsTypes'
import { cmsItemTitle, cmsItemImage, slugifyCms } from '../cmsTypes'
import { collectionScheduleWindow, matchesSearch, scheduleStateFor } from '@webbuilder/shared-types'
import { CmsItemSettings } from './CmsItemSettings'
import { useToast } from '../../Toast'
import { useI18n } from '../../../lib/i18n'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import type { BillingPlan } from '../../../lib/api'
import { SITE_PLAN_LIMITS, usageState } from '../../../lib/planLimits'
import { PlanUsageSummary } from '../PlanUsageSummary'
import { PlanLimitModal } from '../PlanLimitModal'
import { StarFilterButton } from '../StarFilterButton'
import { ConfirmDialog } from '../ConfirmDialog'
import { usePaged, Pagination } from '../Pagination'
import { HeaderRefreshButton, ListToolbar, SelectCheckbox, StarButton, useListSelection } from '../ListSelection'
import { readStarredIds, setStarred as setStarredIds } from '../../../lib/dashboardStars'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import { IconAdjustmentsHorizontal, IconArrowLeft, IconCalendarEvent, IconCheck, IconChevronDown, IconCopy, IconDatabase, IconDots, IconExternalLink, IconFiles, IconFolderOff, IconLayoutGrid, IconLink, IconPencil, IconPlus, IconSearch, IconSettings, IconStar, IconTrash, IconWorld, IconX } from '@tabler/icons-react'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

function cmsDateValue(value: string | undefined): number {
  const time = value ? new Date(value).getTime() : 0
  return Number.isFinite(time) ? time : 0
}

function itemCreatedAt(item: CmsItem): number {
  return cmsDateValue(item.values.createdAt || item.values.created || item.values.date)
}

function itemUpdatedAt(item: CmsItem): number {
  return cmsDateValue(item.values.updatedAt) || itemCreatedAt(item)
}

function collectionCreatedAt(collection: CmsCollection): number {
  const own = cmsDateValue(collection.createdAt)
  if (own) return own
  const dates = collection.items.map(itemCreatedAt).filter(Boolean)
  return dates.length ? Math.min(...dates) : 0
}

function collectionUpdatedAt(collection: CmsCollection): number {
  const own = cmsDateValue(collection.updatedAt)
  if (own) return own
  return Math.max(0, ...collection.items.map(itemUpdatedAt))
}

function formatCmsDate(time: number): string {
  return time ? formatDashboardListDate(time) : '—'
}

function formatCmsDateTime(time: number): string {
  return time ? formatDashboardListDate(time) : '—'
}

function isPublished(item: CmsItem): boolean {
  return item.values.status === 'Público' || item.values.status === 'Publicado'
}

export function CmsSection({
  projectId,
  token,
  listResetKey,
  collections,
  billingPlan,
  saveCollections,
  collectionUrlFor,
  itemUrlFor,
  onDeleteCollection,
  onOpenCms,
  onOpenPlans,
  onReload,
  reloading,
}: {
  /** Para guardar los destacados: viven por proyecto. Ver [[dashboardStars]]. */
  projectId: string
  /** Para subir la portada de una ficha a R2 en vez de meterla en el proyecto. */
  token: string
  /** Cambia al pulsar de nuevo "Contenido" en el sidebar: cierra cualquier
      colección, post o ajuste abierto y devuelve el índice principal. */
  listResetKey?: number | undefined
  /** Vuelve a pedir el proyecto: lo dispara «Actualizar» en la barra. */
  onReload?: (() => void) | undefined
  reloading?: boolean | undefined
  collections: CmsCollection[]
  billingPlan: BillingPlan
  saveCollections: (next: CmsCollection[]) => void | Promise<void>
  collectionUrlFor?: (collectionId: string) => string | null
  /** Published URL of one item, or null when the site is not published yet. */
  itemUrlFor?: ((collectionId: string, item: CmsItem) => string | null) | undefined
  onDeleteCollection?: (collectionId: string) => void | Promise<void>
  /** `itemId: 'new'` abre la colección con una entrada nueva en borrador; `newCollection`, la ventana de crear colección. */
  onOpenCms: (target?: { collectionId: string; itemId?: string } | { newCollection: true }) => void
  onOpenPlans: () => void
}) {
  const { showToast } = useToast()
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('cms.searchPlaceholder')
  const itemSearchPlaceholder = useSearchPlaceholder('cms.item.searchPlaceholder')

  /* Dos listas, dos juegos de destacados: una colección y un elemento son cosas
     distintas y comparten pantalla, así que no pueden compartir cajón. Ver
     [[dashboardStars]]. */
  const [starredCollections, setStarredCollections] = useState<Set<string>>(() => readStarredIds(projectId, 'collections'))
  const [starredItems, setStarredItems] = useState<Set<string>>(() => readStarredIds(projectId, 'cms-items'))
  const toggleCollectionStar = (id: string) => {
    setStarredCollections(new Set(setStarredIds(projectId, 'collections', [id], !starredCollections.has(id))))
  }
  const toggleItemStar = (id: string) => {
    setStarredItems(new Set(setStarredIds(projectId, 'cms-items', [id], !starredItems.has(id))))
  }
  /* «Sólo destacados» recorta sobre lo que hay: las dos listas se paginan en
     memoria, así que no se pierde nada de otras páginas. */
  const [collectionsStarredOnly, setCollectionsStarredOnly] = useState(false)
  const [itemsStarredOnly, setItemsStarredOnly] = useState(false)
  const [collectionToDelete, setCollectionToDelete] = useState<CmsCollection | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [seoItemId, setSeoItemId] = useState<string | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [listMenu, setListMenu] = useState({ top: 0, left: 0, up: false, maxHeight: 0 })
  const toggleRowMenu = (event: ReactMouseEvent<HTMLButtonElement>, id: string, menuHeight: number) => {
    event.stopPropagation()
    if (menuOpenId !== id) {
      const rect = event.currentTarget.getBoundingClientRect()
      const menuWidth = 184
      const edge = 12
      const gap = 8
      const spaceBelow = window.innerHeight - rect.bottom - edge
      const spaceAbove = rect.top - edge
      const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
      const top = up
        ? Math.max(edge, rect.top - menuHeight - gap)
        : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
      const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
      setListMenu({ top, left, up, maxHeight: Math.max(114, up ? spaceAbove - gap : spaceBelow - gap) })
    }
    setMenuOpenId(menuOpenId === id ? null : id)
  }
  const [searchQuery, setSearchQuery] = useState('')
  const [collectionFilter, setCollectionFilter] = useState<'all' | 'withItems' | 'empty'>('all')
  const [collectionSort, setCollectionSort] = useState<'default' | 'name' | 'nameDesc' | 'mostItems' | 'fewestItems' | 'createdRecent' | 'createdOldest' | 'updatedRecent' | 'updatedOldest'>('default')
  const [filterMenuOpen, setFilterMenuOpen] = useState(false)
  const [itemSearchQuery, setItemSearchQuery] = useState('')
  const [itemFilter, setItemFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [itemSort, setItemSort] = useState<'default' | 'name' | 'nameDesc' | 'recent' | 'oldest' | 'updatedRecent' | 'updatedOldest' | 'status'>('default')
  const [itemFilterMenuOpen, setItemFilterMenuOpen] = useState(false)
  /* El identificador del cupo y no su rótulo: el aviso lo traduce al
     pintarse. Con la palabra en español dentro del estado había que
     traducirla en los dos sitios que abren el aviso. */
  const [limitResource, setLimitResource] = useState<'collections' | 'items' | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  const itemSearchInputRef = useRef<HTMLInputElement>(null)
  const itemFilterMenuRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const horizontalDragRef = useRef<{ pointerId: number; startX: number; scrollLeft: number } | null>(null)

  /* En celular el fondo gris visible alrededor de la sección pertenece a
   * `.dash-main`, fuera de este componente. Escuchar ahí permite que un gesto
   * iniciado en ese margen también desplace la tabla. Se decide la dirección
   * antes de cancelar nada para conservar intacto el scroll vertical. */
  useEffect(() => {
    const scroller = tableScrollRef.current
    const frame = scroller?.closest<HTMLElement>('.dash-main')
    if (!scroller || !frame) return

    let gesture: { x: number; y: number; scrollLeft: number; horizontal: boolean | null } | null = null
    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0]
      const target = event.target as Element | null
      if (!touch || target?.closest('.dash-cms-table-scroll, button, a, input, select, textarea')) return
      gesture = { x: touch.clientX, y: touch.clientY, scrollLeft: scroller.scrollLeft, horizontal: null }
    }
    const onTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0]
      if (!gesture || !touch) return
      const dx = gesture.x - touch.clientX
      const dy = gesture.y - touch.clientY
      if (gesture.horizontal === null && Math.max(Math.abs(dx), Math.abs(dy)) >= 6) {
        gesture.horizontal = Math.abs(dx) > Math.abs(dy)
      }
      if (!gesture.horizontal) return
      scroller.scrollLeft = gesture.scrollLeft + dx
      event.preventDefault()
    }
    const endTouch = () => { gesture = null }

    frame.addEventListener('touchstart', onTouchStart, { passive: true })
    frame.addEventListener('touchmove', onTouchMove, { passive: false })
    frame.addEventListener('touchend', endTouch, { passive: true })
    frame.addEventListener('touchcancel', endTouch, { passive: true })
    return () => {
      frame.removeEventListener('touchstart', onTouchStart)
      frame.removeEventListener('touchmove', onTouchMove)
      frame.removeEventListener('touchend', endTouch)
      frame.removeEventListener('touchcancel', endTouch)
    }
  }, [openId, collections.length])

  /** Permite desplazar la tabla aunque el gesto horizontal empiece en el
   * contenedor de CMS y no exactamente sobre una fila. */
  const handleCmsWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const scroller = tableScrollRef.current
    if (!scroller || (event.target as Element).closest('.dash-cms-table-scroll')) return
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.shiftKey ? event.deltaY : 0
    if (!delta || scroller.scrollWidth <= scroller.clientWidth) return
    scroller.scrollLeft += delta
    event.preventDefault()
  }

  const handleCmsPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as Element
    const scroller = tableScrollRef.current
    if (!scroller || target.closest('button, a, input, select, textarea, .dash-cms-table-scroll')) return
    horizontalDragRef.current = { pointerId: event.pointerId, startX: event.clientX, scrollLeft: scroller.scrollLeft }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleCmsPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = horizontalDragRef.current
    const scroller = tableScrollRef.current
    if (!drag || drag.pointerId !== event.pointerId || !scroller) return
    scroller.scrollLeft = drag.scrollLeft + drag.startX - event.clientX
  }

  const endCmsPointerDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (horizontalDragRef.current?.pointerId === event.pointerId) horizontalDragRef.current = null
  }

  useEffect(() => {
    setOpenId(null)
    setSeoItemId(null)
    setMenuOpenId(null)
    setItemFilterMenuOpen(false)
  }, [listResetKey])

  useEffect(() => {
    if (!menuOpenId) return
    function onDown(e: PointerEvent) {
      const el = e.target as Element | null
      if (!el?.closest?.('.dash-project-row-menu-wrap') && !el?.closest?.('.dash-cms-row-menu-portal')) setMenuOpenId(null)
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setMenuOpenId(null) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [menuOpenId])

  useEffect(() => {
    if (!filterMenuOpen) return
    function onDown(event: PointerEvent) {
      if (!filterMenuRef.current?.contains(event.target as Node)) setFilterMenuOpen(false)
    }
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') setFilterMenuOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [filterMenuOpen])

  useEffect(() => {
    if (!itemFilterMenuOpen) return
    function onDown(event: PointerEvent) {
      if (!itemFilterMenuRef.current?.contains(event.target as Node)) setItemFilterMenuOpen(false)
    }
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') setItemFilterMenuOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [itemFilterMenuOpen])

  useEffect(() => {
    setItemSearchQuery('')
    setItemFilter('all')
    setItemSort('default')
    setItemFilterMenuOpen(false)
  }, [openId])

  const openCollection = collections.find((c) => c.id === openId) ?? null
  /* Sin acentos: nadie los escribe al buscar, y «articulo» tenía que encontrar
     «Artículo». Ver `matchesSearch`. */
  const normalizedSearch = searchQuery.trim()
  const filteredCollections = collections
    .filter((collection) => !normalizedSearch || (
      matchesSearch(collection.name, normalizedSearch)
      || matchesSearch(collection.description, normalizedSearch)
    ))
    .filter((collection) => (
      collectionFilter === 'all'
      || (collectionFilter === 'withItems' && collection.items.length > 0)
      || (collectionFilter === 'empty' && collection.items.length === 0)
    ))
    .filter((collection) => !collectionsStarredOnly || starredCollections.has(collection.id))
    .sort((a, b) => {
      if (collectionSort === 'name') return a.name.localeCompare(b.name, 'es-MX')
      if (collectionSort === 'nameDesc') return b.name.localeCompare(a.name, 'es-MX')
      if (collectionSort === 'mostItems') return b.items.length - a.items.length
      if (collectionSort === 'fewestItems') return a.items.length - b.items.length
      if (collectionSort === 'createdRecent' || collectionSort === 'createdOldest') {
        const diff = collectionCreatedAt(a) - collectionCreatedAt(b)
        return collectionSort === 'createdOldest' ? diff : -diff
      }
      if (collectionSort === 'updatedRecent' || collectionSort === 'updatedOldest') {
        const diff = collectionUpdatedAt(a) - collectionUpdatedAt(b)
        return collectionSort === 'updatedOldest' ? diff : -diff
      }
      return collections.indexOf(a) - collections.indexOf(b)
    })
  const normalizedItemSearch = itemSearchQuery.trim()
  const filteredItems = openCollection
    ? openCollection.items
      .filter((item) => !normalizedItemSearch || (
        matchesSearch(cmsItemTitle(openCollection, item), normalizedItemSearch)
        || matchesSearch(item.values.slug, normalizedItemSearch)
      ))
      .filter((item) => (
        itemFilter === 'all'
        || (itemFilter === 'published' && isPublished(item))
        || (itemFilter === 'draft' && !isPublished(item))
      ))
      .filter((item) => !itemsStarredOnly || starredItems.has(item.id))
      .sort((a, b) => {
        if (itemSort === 'name') return cmsItemTitle(openCollection, a).localeCompare(cmsItemTitle(openCollection, b), 'es-MX')
        if (itemSort === 'nameDesc') return cmsItemTitle(openCollection, b).localeCompare(cmsItemTitle(openCollection, a), 'es-MX')
        if (itemSort === 'recent' || itemSort === 'oldest') return itemSort === 'oldest' ? itemCreatedAt(a) - itemCreatedAt(b) : itemCreatedAt(b) - itemCreatedAt(a)
        if (itemSort === 'updatedRecent' || itemSort === 'updatedOldest') return itemSort === 'updatedOldest' ? itemUpdatedAt(a) - itemUpdatedAt(b) : itemUpdatedAt(b) - itemUpdatedAt(a)
        if (itemSort === 'status') return Number(isPublished(b)) - Number(isPublished(a))
        return openCollection.items.indexOf(a) - openCollection.items.indexOf(b)
      })
    : []

  const pagedItems = usePaged(filteredItems, 10, `${openId}|${itemSearchQuery}|${itemFilter}|${itemSort}|${itemsStarredOnly}`)

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar —que es lo único que tiene que
     haber en pantalla. Vuelve con la primera fila, o mientras quede algo
     escrito: si no, una búsqueda sin resultados se llevaría por delante la
     caja donde estaba escrita y no habría cómo deshacerla. */
  const showCollectionToolbar = collections.length > 0 || normalizedSearch !== ''
  const showItemToolbar = (openCollection?.items.length ?? 0) > 0 || normalizedItemSearch !== ''
  const listSelection = useListSelection(pagedItems.pageItems.map((item) => item.id))
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)

  /** Borra los elementos marcados en una sola escritura de la colección. */
  async function deleteSelectedItems(collectionId: string) {
    const ids = new Set(listSelection.ids)
    setBulkDeleteOpen(false)
    listSelection.clear()
    if (ids.size === 0) return
    // Un único `mutateItems` y no uno por elemento: cada llamada guarda la
    // colección entera, así que en bucle se mandarían N escrituras del mismo
    // documento y la última podría pisar a las anteriores.
    await mutateItems(collectionId, (items) => items.filter((item) => !ids.has(item.id)))
    showToast(ids.size === 1 ? t('cms.item.bulkDeleted.one') : t('cms.item.bulkDeleted.many', { count: ids.size }))
  }
  const pagedCollections = usePaged(filteredCollections, 10, `${searchQuery}|${collectionFilter}|${collectionSort}|${collectionsStarredOnly}`)

  const totalItems = collections.reduce((sum, collection) => sum + collection.items.length, 0)
  function handleNewCollection() {
    if (usageState(collections.length, SITE_PLAN_LIMITS[billingPlan].collections) === 'full') { setLimitResource('collections'); return }
    /* Directo a la ventana de crear: llevar al CMS y que allí se busque el
       botón otra vez era un paso de más. */
    onOpenCms({ newCollection: true })
  }
  function handleNewItem(collectionId: string) {
    if (usageState(totalItems, SITE_PLAN_LIMITS[billingPlan].cmsItems) === 'full') { setLimitResource('items'); return }
    /* La entrada nueva ya creada, en borrador, con su ficha abierta. */
    onOpenCms({ collectionId, itemId: 'new' })
  }

  function setCollectionColumnSort(column: 'name' | 'items' | 'created' | 'updated') {
    if (column === 'name') setCollectionSort((current) => current === 'name' ? 'nameDesc' : 'name')
    if (column === 'items') setCollectionSort((current) => current === 'mostItems' ? 'fewestItems' : 'mostItems')
    if (column === 'created') setCollectionSort((current) => current === 'createdRecent' ? 'createdOldest' : 'createdRecent')
    if (column === 'updated') setCollectionSort((current) => current === 'updatedRecent' ? 'updatedOldest' : 'updatedRecent')
  }

  function setItemColumnSort(column: 'name' | 'status' | 'created' | 'updated') {
    if (column === 'name') setItemSort((current) => current === 'name' ? 'nameDesc' : 'name')
    if (column === 'status') setItemSort((current) => current === 'status' ? 'default' : 'status')
    if (column === 'created') setItemSort((current) => current === 'recent' ? 'oldest' : 'recent')
    if (column === 'updated') setItemSort((current) => current === 'updatedRecent' ? 'updatedOldest' : 'updatedRecent')
  }

  function mutateItems(collectionId: string, updater: (items: CmsItem[]) => CmsItem[]): void | Promise<void> {
    return saveCollections(collections.map((c) => (c.id === collectionId ? { ...c, items: updater(c.items) } : c)))
  }

  function duplicateItem(collection: CmsCollection, itemId: string) {
    const item = collection.items.find((i) => i.id === itemId)
    if (!item) return
    const titleField = collection.fields.find((f) => f.type === 'title')
    const title = cmsItemTitle(collection, item)
    const values: Record<string, string> = { ...item.values }
    const copy = t('cms.item.copySuffix')
    if (titleField) values[titleField.id] = `${title} (${copy})`
    else if (values.title) values.title = `${title} (${copy})`
    const base = slugifyCms(`${title}-${copy}`)
    const taken = new Set(collection.items.map((i) => i.values.slug).filter(Boolean))
    let slug = base
    let n = 2
    while (taken.has(slug)) { slug = `${base}-${n}`; n += 1 }
    values.slug = slug
    // Fuego y olvido a propósito: `saveCollections` ya avisa si el guardado
    // falla, así que esperar aquí sólo serviría para no hacer nada con ello.
    void mutateItems(collection.id, (items) => [...items, { id: crypto.randomUUID(), values }])
  }

  async function deleteItem(collectionId: string, itemId: string) {
    setMenuOpenId(null)
    await mutateItems(collectionId, (items) => items.filter((i) => i.id !== itemId))
    showToast(t('cms.item.deleted'))
  }

  function saveItemSeo(collectionId: string, itemId: string, seo: { seoTitle: string; seoDescription: string; seoImage: string }) {
    void mutateItems(collectionId, (items) => items.map((i) => (i.id === itemId ? { ...i, values: { ...i.values, ...seo } } : i)))
  }

  // ── Per-item SEO settings ──
  if (openCollection && seoItemId) {
    const item = openCollection.items.find((i) => i.id === seoItemId)
    if (item) {
      return (
        <div style={{ background: 'var(--dash-panel)', border: '1px solid var(--dash-border)', borderRadius: 14, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 116px)', overflow: 'hidden' }}>
          <CmsItemSettings
            key={seoItemId}
            title={cmsItemTitle(openCollection, item)}
            item={item}
            token={token}
            projectId={projectId}
            onBack={() => setSeoItemId(null)}
            onSave={(seo) => { saveItemSeo(openCollection.id, seoItemId, seo); setSeoItemId(null) }}
          />
        </div>
      )
    }
  }

  // ── Items (posts) of an open collection ──
  if (openCollection) {
    /* El estado de vigencia de una ficha, con la MISMA regla que usa el editor
       y que se aplica en la página publicada: viene de `@webbuilder/shared-types`
       y no de una copia local. */
    const scheduleWindow = collectionScheduleWindow(openCollection)
    const scheduleState = (item: CmsItem) => scheduleStateFor(item.values, scheduleWindow)
    return (
      <div className="dash-projects-view dash-pages-view dash-cms-pages-view" onWheel={handleCmsWheel} onPointerDown={handleCmsPointerDown} onPointerMove={handleCmsPointerMove} onPointerUp={endCmsPointerDrag} onPointerCancel={endCmsPointerDrag}>
        {limitResource ? <PlanLimitModal resource={t(`cms.limit.${limitResource}`)} currentPlan={billingPlan} onClose={() => setLimitResource(null)} onOpenPlans={onOpenPlans} /> : null}
        <DashboardPageHeader
          title={openCollection.name}
          subtitle={t('cms.item.subtitle')}
          actions={(
            <>
              {onReload ? <HeaderRefreshButton refreshing={reloading ?? false} onRefresh={onReload} /> : null}
              <button className="dash-secondary-btn dash-detail-back" type="button" onClick={() => { setOpenId(null); setMenuOpenId(null) }}>
                <IconArrowLeft size={14} /> {t('cms.backToCollections')}
              </button>
            </>
          )}
        />

        {showItemToolbar ? (
          <header className="dash-toolbar dash-layered-toolbar">
            {/* «Actualizar» y el buscador van juntos, en un solo hijo: la barra
                reparte con `space-between`, y con tres el buscador se iba al medio y
                los filtros caían a un segundo renglón. */}
            <div className="dash-toolbar-lead">
              <div className="dash-toolbar-search">
              <button
              type="button"
              className="dash-toolbar-search-icon"
              aria-label={t('cms.item.searchAria')}
              onClick={() => itemSearchInputRef.current?.focus()}
              >
              <IconSearch size={16} />
              </button>
              <input
              ref={itemSearchInputRef}
              value={itemSearchQuery}
              onChange={(event) => setItemSearchQuery(event.target.value)}
              placeholder={itemSearchPlaceholder}
              onKeyDown={(event) => {
              if (event.key === 'Escape') {
              setItemSearchQuery('')
              itemSearchInputRef.current?.blur()
              }
              }}
              />
              {itemSearchQuery ? (
              <button type="button" className="dash-toolbar-search-clear" aria-label={t('cms.clearSearch')} onClick={() => { setItemSearchQuery(''); itemSearchInputRef.current?.focus() }}>
              <IconX size={13} />
              </button>
              ) : null}
              </div>
            </div>
            <div className="dash-toolbar-controls">
              <StarFilterButton active={itemsStarredOnly} onToggle={() => setItemsStarredOnly((only) => !only)} label={t('cms.item.starred')} />
              <div className="dash-filter-wrap" ref={itemFilterMenuRef}>
                <button
                  className="dash-filter-trigger"
                  type="button"
                  aria-label={t('cms.filtersAria')}
                  data-tooltip={t('cms.filtersAria')}
                  aria-expanded={itemFilterMenuOpen}
                  onClick={() => setItemFilterMenuOpen((open) => !open)}
                >
                  <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                  {itemFilter !== 'all' || itemsStarredOnly ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
                </button>

                {itemFilterMenuOpen ? (
                  <div className="dash-filter-popover" role="dialog" aria-label={t('cms.item.filtersDialogAria')}>
                    <div className="dash-filter-popover-head">
                      <strong>{t('cms.item.filterTitle')}</strong>
                      <button type="button" aria-label={t('cms.closeFilters')} onClick={() => setItemFilterMenuOpen(false)}><IconX size={15} /></button>
                    </div>

                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('cms.item.filterStatus')}</span>
                      <div className="dash-filter-options">
                        {([
                          ['all', 'cms.item.status.all', IconLayoutGrid],
                          ['published', 'cms.item.status.published', IconWorld],
                          ['draft', 'cms.item.status.draft', IconPencil],
                        ] as const).map(([value, labelKey, Icon]) => (
                          <button key={value} className={itemFilter === value ? 'active' : ''} type="button" onClick={() => setItemFilter(value)}>
                            <Icon size={14} />
                            <span>{t(labelKey)}</span>
                            {itemFilter === value ? <IconCheck size={14} /> : null}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="dash-filter-section">
                      <span className="dash-filter-section-title">{t('cms.item.filterStarred')}</span>
                      <div className="dash-filter-options">
                        <button className={!itemsStarredOnly ? 'active' : ''} type="button" onClick={() => setItemsStarredOnly(false)}><IconLayoutGrid size={14} /><span>{t('cms.item.allStars')}</span>{!itemsStarredOnly ? <IconCheck size={14} /> : null}</button>
                        <button className={itemsStarredOnly ? 'active' : ''} type="button" onClick={() => setItemsStarredOnly(true)}><IconStar size={14} /><span>{t('cms.item.onlyStarred')}</span>{itemsStarredOnly ? <IconCheck size={14} /> : null}</button>
                      </div>
                    </div>

                    {itemFilter !== 'all' || itemsStarredOnly ? (
                      <button className="dash-filter-reset" type="button" onClick={() => { setItemFilter('all'); setItemsStarredOnly(false) }}>
                        {t('cms.resetFilters')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <button className="dash-primary-btn dash-toolbar-create" type="button" aria-label={t('cms.item.newAria')} onClick={() => handleNewItem(openCollection.id)}>
                <IconPlus size={15} />
                <span className="dash-btn-label dash-btn-label--desktop">{t('cms.item.new')}</span>
                <span className="dash-btn-label dash-btn-label--compact">{t('cms.item.newShort')}</span>
              </button>
            </div>
          </header>
        ) : null}

        {bulkDeleteOpen ? (
          <ConfirmDialog
            title={listSelection.count === 1 ? t('cms.item.bulkDelete.titleOne') : t('cms.item.bulkDelete.titleMany', { count: listSelection.count })}
            tone="danger"
            confirmLabel={t('cms.delete')}
            confirmingLabel={t('cms.deleting')}
            message={t('cms.item.bulkDelete.body')}
            onConfirm={() => deleteSelectedItems(openCollection.id)}
            onCancel={() => setBulkDeleteOpen(false)}
          />
        ) : null}

        {openCollection.items.length > 0 ? (
          <ListToolbar
            selection={listSelection}
            actions={[{ icon: <IconTrash size={14} />, label: t('cms.delete'), danger: true, onClick: () => setBulkDeleteOpen(true) }]}
          />
        ) : null}

        {openCollection.items.length === 0 ? (
          <div className="dash-placeholder">
            <IconFiles size={34} aria-hidden="true" />
            <strong>{t('cms.item.emptyTitle')}</strong>
            <span>{t('cms.item.emptyBody')}</span>
            <button className="dash-primary-btn" type="button" onClick={() => handleNewItem(openCollection.id)}><IconPlus size={15} /> {t('cms.item.emptyCta')}</button>
          </div>
        ) : filteredItems.length === 0 ? (
          <section className="dash-placeholder">
            <IconSearch size={34} aria-hidden="true" />
            <strong>{t('cms.item.noResultsTitle')}</strong>
            <span>{t('cms.item.noResultsBody')}</span>
            <button className="dash-secondary-btn" type="button" onClick={() => { setItemSearchQuery(''); setItemFilter('all'); setItemSort('default'); setItemsStarredOnly(false) }}>{t('cms.clearFilters')}</button>
          </section>
        ) : (
          <>
          {/* Encima de la tabla, donde se decide qué mirar. */}
          <Pagination page={pagedItems.page} totalPages={pagedItems.totalPages} total={pagedItems.total} start={pagedItems.start} end={pagedItems.end} onPage={pagedItems.setPage} unit={t('cms.item.unit')} />
          <div className="dash-cms-table-scroll" ref={tableScrollRef}>
          <div className="dash-data-table dash-cms-items-data-table has-selection has-stars">
            <div className="dash-data-header" role="row">
              <span className="dash-data-select-cell">
                <SelectCheckbox
                  checked={listSelection.allVisible}
                  mixed={listSelection.someVisible}
                  label={listSelection.allVisible ? t('cms.deselectAll') : t('cms.selectAll')}
                  onChange={listSelection.toggleAll}
                />
              </span>
              <button className={itemSort === 'name' || itemSort === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => setItemColumnSort('name')}>
                {t('cms.col.name')} <IconChevronDown className={itemSort === 'name' ? 'is-asc' : ''} size={13} />
              </button>
              <span>{t('cms.col.url')}</span>
              <button className={itemSort === 'status' ? 'is-active' : ''} type="button" onClick={() => setItemColumnSort('status')}>
                {t('cms.col.status')} <IconChevronDown size={13} />
              </button>
              <button className={itemSort === 'recent' || itemSort === 'oldest' ? 'is-active' : ''} type="button" onClick={() => setItemColumnSort('created')}>
                {t('cms.col.created')} <IconChevronDown className={itemSort === 'oldest' ? 'is-asc' : ''} size={13} />
              </button>
              <button className={itemSort === 'updatedRecent' || itemSort === 'updatedOldest' ? 'is-active' : ''} type="button" onClick={() => setItemColumnSort('updated')}>
                {t('cms.col.updated')} <IconChevronDown className={itemSort === 'updatedOldest' ? 'is-asc' : ''} size={13} />
              </button>
              <span className="dash-data-header-actions">{t('cms.col.actions')}</span>
            </div>
            <div className="dash-project-list">
            {pagedItems.pageItems.map((item) => {
              const createdTime = itemCreatedAt(item)
              const updatedTime = itemUpdatedAt(item)
              const published = isPublished(item)
              const itemUrl = itemUrlFor?.(openCollection.id, item) ?? null
              // Sin página dinámica publicada no hay URL que abrir, pero la ruta
              // que tendrá sí se puede mostrar.
              const itemPath = itemUrl ? new URL(itemUrl).pathname : (item.values.slug ? `/${item.values.slug.replace(/^\/+/, '')}` : t('cms.item.noUrl'))
              return (
              <article className={`dash-project-row dash-data-row dash-loaded-content-in${listSelection.isSelected(item.id) ? ' is-selected' : ''}`} key={item.id} onClick={openOnRowClick(() => onOpenCms({ collectionId: openCollection.id, itemId: item.id }))}>
                <span className="dash-data-select-cell">
                  <SelectCheckbox
                    checked={listSelection.isSelected(item.id)}
                    label={t('cms.item.selectOne', { name: cmsItemTitle(openCollection, item) || t('cms.item.thisItem') })}
                    onChange={() => listSelection.toggle(item.id)}
                  />
                  <StarButton
                    starred={starredItems.has(item.id)}
                    label={t('cms.item.starOne', { name: cmsItemTitle(openCollection, item) || t('cms.item.thisItem') })}
                    onToggle={() => toggleItemStar(item.id)}
                  />
                </span>
                <div className="dash-project-row-main" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onOpenCms({ collectionId: openCollection.id, itemId: item.id }) }}>
                  {cmsItemImage(openCollection, item) ? (
                    <span className="dash-project-row-thumb dash-page-project-thumb"><img src={cmsItemImage(openCollection, item)!} alt="" loading="lazy" /></span>
                  ) : (
                    <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                      <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true"><IconFiles size={13} /></span>
                    </span>
                  )}
                  <span className="dash-project-row-copy">
                    <span className="dash-project-row-title"><strong>{cmsItemTitle(openCollection, item)}</strong></span>
                  </span>
                </div>
                <div className="dash-list-row-meta dash-cms-item-row-meta">
                  {/* Dos botones y no la dirección escrita.
                      La URL de una ficha es larga y repetitiva —la misma base y
                      el mismo prefijo de colección en todas—, así que ocupaba una
                      columna ancha para enseñar, recortado con puntos suspensivos,
                      lo poco que cambia de una fila a otra. Lo que se hace con
                      ella es copiarla o abrirla; ambas caben en 84px, y los 150
                      que sobran se los queda el título, que es lo que sí se lee.
                      La dirección entera sigue accesible en el `title`. */}
                  <span className="dash-data-cell dash-cms-url-actions" data-label={t('cms.col.url')} title={itemUrl || itemPath}>
                    {itemUrl ? (
                      <>
                        <button
                          type="button"
                          className="dash-icon-btn dash-cms-url-btn"
                          aria-label={t('cms.item.copyUrlAria', { name: cmsItemTitle(openCollection, item) })}
                          data-tooltip={t('cms.copyUrl')}
                          onClick={() => { void navigator.clipboard?.writeText(itemUrl); showToast(t('cms.linkCopied')) }}
                        >
                          <IconCopy size={14} />
                        </button>
                        <a
                          className="dash-icon-btn dash-cms-url-btn"
                          href={itemUrl}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={t('cms.item.openAria', { name: cmsItemTitle(openCollection, item) })}
                          data-tooltip={t('cms.open')}
                        >
                          <IconExternalLink size={14} />
                        </a>
                      </>
                    ) : (
                      /* Sin sitio publicado no hay nada que copiar ni que abrir.
                         Un guion lo dice sin dejar dos botones muertos. */
                      <span className="dash-cms-url-none" aria-label={t('cms.item.noUrlYet')}>—</span>
                    )}
                  </span>
                  <span className="dash-data-cell dash-cms-status-cell" data-label={t('cms.col.status')}>
                    <span className={`dash-page-row-badge ${published ? 'cms-live' : 'cms-draft'}`}>{published ? t('cms.item.published') : t('cms.item.draft')}</span>
                    {/* La vigencia, al lado del estado y no en su lugar: son dos
                        cosas distintas —una ficha publicada puede estar
                        terminada— y sustituir una por otra escondería la que no
                        se enseñara. Sólo sale si la colección declara fechas. */}
                    {scheduleState(item) ? (
                      <span className={`dash-page-row-badge cms-schedule-${scheduleState(item)}`}>
                        {t(`cms.schedule.${scheduleState(item)!}`)}
                      </span>
                    ) : null}
                  </span>
                  <time className="dash-data-cell dash-list-date-cell" data-label={t('cms.col.created')} dateTime={createdTime ? new Date(createdTime).toISOString() : undefined}><IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatCmsDate(createdTime)}</time>
                  <time className="dash-data-cell dash-list-date-cell dash-page-updated-cell" data-label={t('cms.col.updated')} dateTime={updatedTime ? new Date(updatedTime).toISOString() : undefined}><IconPencil className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatCmsDateTime(updatedTime)}</time>
                </div>
                <div className="dash-project-row-actions">
                  <button className="dash-secondary-btn dash-project-row-action" type="button" onClick={() => onOpenCms({ collectionId: openCollection.id, itemId: item.id })}><IconFiles size={14} /> <span className="dash-project-row-action-label">{t('cms.item.editShort')}</span></button>
                  <div className={`dash-project-row-menu-wrap${menuOpenId === item.id ? ' is-open' : ''}`}>
                    <button className="project-menu-btn dash-icon-btn dash-page-options-btn" type="button" aria-label={t('cms.moreOptions')} data-tooltip={t('cms.options')} onClick={(event) => toggleRowMenu(event, item.id, 114)}><IconDots size={16} /></button>
                    {menuOpenId === item.id ? createPortal(
                      <div className={`project-menu dash-project-row-menu dash-cms-row-menu-portal${listMenu.up ? ' is-up' : ''}`} style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}>
                        <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); setSeoItemId(item.id) }}><IconSettings size={14} /> {t('cms.item.seo')}</button>
                        <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); duplicateItem(openCollection, item.id) }}><IconCopy size={14} /> {t('cms.item.duplicate')}</button>
                        <button className="project-menu-item danger" type="button" onClick={() => { void deleteItem(openCollection.id, item.id) }}><IconTrash size={14} /> {t('cms.delete')}</button>
                      </div>,
                      document.body,
                    ) : null}
                  </div>
                </div>
              </article>
              )
            })}
            </div>
          </div>
          </div>
          {/* Y al pie, donde se termina de leer. */}
          <Pagination page={pagedItems.page} totalPages={pagedItems.totalPages} total={pagedItems.total} start={pagedItems.start} end={pagedItems.end} onPage={pagedItems.setPage} unit={t('cms.item.unit')} alwaysShow />
          </>
        )}
      </div>
    )
  }

  // ── Collections ──
  return (
    <div className="dash-projects-view dash-pages-view dash-cms-pages-view" onWheel={handleCmsWheel} onPointerDown={handleCmsPointerDown} onPointerMove={handleCmsPointerMove} onPointerUp={endCmsPointerDrag} onPointerCancel={endCmsPointerDrag}>
      {limitResource ? <PlanLimitModal resource={t(`cms.limit.${limitResource}`)} currentPlan={billingPlan} onClose={() => setLimitResource(null)} onOpenPlans={onOpenPlans} /> : null}
      {collectionToDelete ? (
        <ConfirmDialog
          title={t('cms.delete.title', { name: collectionToDelete.name })}
          tone="danger"
          confirmLabel={t('cms.delete.confirm')}
          confirmingLabel={t('cms.deleting')}
          cancelLabel={t('cms.cancel')}
          message={(() => {
            const items = collectionToDelete.items
            const names = items.slice(0, 5).map((it) => cmsItemTitle(collectionToDelete, it) || t('cms.untitled'))
            return (
              <>
                {/* Sin el <b> de antes: partir la frase en tres claves para
                    conservar la negrita ata el orden de las palabras, que es
                    justo lo que cambia al traducir. */}
                <p style={{ margin: '0 0 12px' }}>{t('cms.delete.body')}</p>
                {items.length > 0 ? (
                  <div style={{ background: 'var(--dash-bg)', border: '1px solid var(--dash-border)', borderRadius: 10, padding: '12px 14px' }}>
                    <strong style={{ color: 'var(--dash-text)' }}>{items.length === 1 ? t('cms.delete.willDeleteOne') : t('cms.delete.willDeleteMany', { count: items.length })}</strong>
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {names.map((name, i) => <li key={i}>{name}</li>)}
                    </ul>
                    {items.length > names.length ? (
                      <span style={{ display: 'block', marginTop: 6, color: 'var(--dash-muted)' }}>{t('cms.delete.andMore', { count: items.length - names.length })}</span>
                    ) : null}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: 'var(--dash-muted)' }}>{t('cms.delete.noItems')}</p>
                )}
              </>
            )
          })()}
          onConfirm={async () => {
            const c = collectionToDelete
            await onDeleteCollection?.(c.id)
            setCollectionToDelete(null)
            showToast(t('cms.collectionDeleted', { name: c.name }))
          }}
          onCancel={() => setCollectionToDelete(null)}
        />
      ) : null}
      <DashboardPageHeader
        title={t('cms.title')}
        subtitle={t('cms.subtitle')}
        actionLayout="split"
        actions={(
          <>
            {onReload ? <HeaderRefreshButton refreshing={reloading ?? false} onRefresh={onReload} /> : null}
            <PlanUsageSummary items={[
              { label: t('cms.usage.collections'), used: collections.length, limit: SITE_PLAN_LIMITS[billingPlan].collections },
              { label: t('cms.usage.items'), used: collections.reduce((total, collection) => total + collection.items.length, 0), limit: SITE_PLAN_LIMITS[billingPlan].cmsItems },
            ]} plan={billingPlan} onOpenPlans={onOpenPlans} />
          </>
        )}
      />

      {showCollectionToolbar ? (
        <header className="dash-toolbar dash-layered-toolbar">
          <div className="dash-toolbar-search">
            <button
              type="button"
              className="dash-toolbar-search-icon"
              aria-label={t('cms.searchAria')}
              onClick={() => searchInputRef.current?.focus()}
            >
              <IconSearch size={16} />
            </button>
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setSearchQuery('')
                  searchInputRef.current?.blur()
                }
              }}
            />
            {searchQuery ? (
              <button type="button" className="dash-toolbar-search-clear" aria-label={t('cms.clearSearch')} onClick={() => { setSearchQuery(''); searchInputRef.current?.focus() }}>
                <IconX size={13} />
              </button>
            ) : null}
          </div>

          <div className="dash-toolbar-controls">
            <StarFilterButton active={collectionsStarredOnly} onToggle={() => setCollectionsStarredOnly((only) => !only)} label={t('cms.starred')} />
            <div className="dash-filter-wrap" ref={filterMenuRef}>
              <button
                className="dash-filter-trigger"
                type="button"
                aria-label={t('cms.filtersAria')}
                data-tooltip={t('cms.filtersAria')}
                aria-expanded={filterMenuOpen}
                onClick={() => setFilterMenuOpen((open) => !open)}
              >
                <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                {collectionFilter !== 'all' || collectionsStarredOnly ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
              </button>

              {filterMenuOpen ? (
                <div className="dash-filter-popover" role="dialog" aria-label={t('cms.filtersDialogAria')}>
                  <div className="dash-filter-popover-head">
                    <strong>{t('cms.filterTitle')}</strong>
                    <button type="button" aria-label={t('cms.closeFilters')} onClick={() => setFilterMenuOpen(false)}><IconX size={15} /></button>
                  </div>

                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('cms.filterContent')}</span>
                    <div className="dash-filter-options">
                      {([
                        ['all', 'cms.filter.all', IconLayoutGrid],
                        ['withItems', 'cms.filter.withItems', IconFiles],
                        ['empty', 'cms.filter.empty', IconFolderOff],
                      ] as const).map(([value, labelKey, Icon]) => (
                        <button key={value} className={collectionFilter === value ? 'active' : ''} type="button" onClick={() => setCollectionFilter(value)}>
                          <Icon size={14} />
                          <span>{t(labelKey)}</span>
                          {collectionFilter === value ? <IconCheck size={14} /> : null}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('cms.filterStarred')}</span>
                    <div className="dash-filter-options">
                      <button className={!collectionsStarredOnly ? 'active' : ''} type="button" onClick={() => setCollectionsStarredOnly(false)}><IconLayoutGrid size={14} /><span>{t('cms.allStars')}</span>{!collectionsStarredOnly ? <IconCheck size={14} /> : null}</button>
                      <button className={collectionsStarredOnly ? 'active' : ''} type="button" onClick={() => setCollectionsStarredOnly(true)}><IconStar size={14} /><span>{t('cms.onlyStarred')}</span>{collectionsStarredOnly ? <IconCheck size={14} /> : null}</button>
                    </div>
                  </div>

                  {collectionFilter !== 'all' || collectionsStarredOnly ? (
                    <button className="dash-filter-reset" type="button" onClick={() => { setCollectionFilter('all'); setCollectionsStarredOnly(false) }}>
                      {t('cms.resetFilters')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <button className="dash-primary-btn dash-toolbar-create" type="button" aria-label={t('cms.newAria')} onClick={handleNewCollection}>
              <IconDatabase size={15} />
              <span className="dash-btn-label dash-btn-label--desktop">{t('cms.new')}</span>
              <span className="dash-btn-label dash-btn-label--compact">{t('cms.newShort')}</span>
            </button>
          </div>
        </header>
      ) : null}

      {collections.length === 0 ? (
        <div className="dash-placeholder">
          <IconDatabase size={34} aria-hidden="true" />
          <strong>{t('cms.emptyTitle')}</strong>
          <span>{t('cms.emptyBody')}</span>
          {/* Se crean en el editor, que es donde se dibuja la plantilla con la
              que salen. El botón es primario igual que los demás vacíos: aquí
              es la única salida de la pantalla. */}
          <button className="dash-primary-btn" type="button" onClick={handleNewCollection}><IconExternalLink size={14} /> {t('cms.emptyCta')}</button>
        </div>
      ) : filteredCollections.length === 0 ? (
        <section className="dash-placeholder">
          <IconSearch size={34} aria-hidden="true" />
          <strong>{t('cms.noResultsTitle')}</strong>
          <span>{t('cms.noResultsBody')}</span>
          <button className="dash-secondary-btn" type="button" onClick={() => { setSearchQuery(''); setCollectionFilter('all'); setCollectionSort('default'); setCollectionsStarredOnly(false) }}>{t('cms.clearFilters')}</button>
        </section>
      ) : (
        <>
        {/* Encima de la tabla, donde se decide qué mirar. */}
        <Pagination page={pagedCollections.page} totalPages={pagedCollections.totalPages} total={pagedCollections.total} start={pagedCollections.start} end={pagedCollections.end} onPage={pagedCollections.setPage} unit={t('cms.unit')} />
        <div className="dash-cms-table-scroll" ref={tableScrollRef}>
        <div className="dash-data-table dash-cms-collections-data-table has-stars-only">
          <div className="dash-data-header" role="row">
            <span className="dash-data-select-cell" />
            <button className={collectionSort === 'name' || collectionSort === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => setCollectionColumnSort('name')}>
              {t('cms.col.name')} <IconChevronDown className={collectionSort === 'name' ? 'is-asc' : ''} size={13} />
            </button>
            <button className={collectionSort === 'mostItems' || collectionSort === 'fewestItems' ? 'is-active' : ''} type="button" onClick={() => setCollectionColumnSort('items')}>
              {t('cms.col.items')} <IconChevronDown className={collectionSort === 'fewestItems' ? 'is-asc' : ''} size={13} />
            </button>
            <button className={collectionSort === 'createdRecent' || collectionSort === 'createdOldest' ? 'is-active' : ''} type="button" onClick={() => setCollectionColumnSort('created')}>
              {t('cms.col.created')} <IconChevronDown className={collectionSort === 'createdOldest' ? 'is-asc' : ''} size={13} />
            </button>
            <button className={collectionSort === 'updatedRecent' || collectionSort === 'updatedOldest' ? 'is-active' : ''} type="button" onClick={() => setCollectionColumnSort('updated')}>
              {t('cms.col.updated')} <IconChevronDown className={collectionSort === 'updatedOldest' ? 'is-asc' : ''} size={13} />
            </button>
            <span className="dash-data-header-actions">{t('cms.col.actions')}</span>
          </div>
          <div className="dash-project-list">
          {pagedCollections.pageItems.map((c) => {
            const previewImages = c.items
              .map((item) => cmsItemImage(c, item))
              .filter((image): image is string => Boolean(image))
              .slice(0, 3)
            const createdTime = collectionCreatedAt(c)
            const updatedTime = collectionUpdatedAt(c)
            return (
            <article className="dash-project-row dash-data-row dash-loaded-content-in" key={c.id} onClick={openOnRowClick(() => setOpenId(c.id))}>
              {/* Sin casilla de selección —las colecciones no se borran en
                  bloque—, así que la celda lleva sólo la estrella. */}
              <span className="dash-data-select-cell">
                <StarButton
                  starred={starredCollections.has(c.id)}
                  label={t('cms.starOne', { name: c.name })}
                  onToggle={() => toggleCollectionStar(c.id)}
                />
              </span>
              <div className="dash-project-row-main" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(c.id) }}>
                {previewImages.length > 0 ? (
                  <span className={`dash-project-row-thumb dash-page-project-thumb dash-cms-preview-thumb${previewImages.length === 1 ? ' is-single' : ' is-stack'}`}>
                    <span className="dash-cms-preview-stack" aria-hidden="true">
                      {previewImages.map((image, index) => (
                        <img className={`dash-cms-preview-image is-layer-${index + 1}`} src={image} alt="" loading="lazy" key={`${image}-${index}`} />
                      ))}
                    </span>
                  </span>
                ) : (
                  <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                    <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true"><IconDatabase size={13} /></span>
                  </span>
                )}
                <span className="dash-project-row-copy">
                  <span className="dash-project-row-title">
                    <strong>{c.name}</strong>
                    <span className="dash-collection-count-compact" aria-label={t('cms.itemCount', { count: c.items.length })}>
                      {c.items.length}
                    </span>
                  </span>
                </span>
              </div>
              <div className="dash-list-row-meta dash-cms-collection-row-meta">
                <span className="dash-data-cell dash-data-count" data-label={t('cms.col.items')}>{c.items.length}</span>
                <time className="dash-data-cell dash-list-date-cell" data-label={t('cms.col.created')} dateTime={createdTime ? new Date(createdTime).toISOString() : undefined}><IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatCmsDate(createdTime)}</time>
                <time className="dash-data-cell dash-list-date-cell dash-page-updated-cell" data-label={t('cms.col.updated')} dateTime={updatedTime ? new Date(updatedTime).toISOString() : undefined}><IconPencil className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatCmsDateTime(updatedTime)}</time>
              </div>
              <div className="dash-project-row-actions">
                <button className="dash-secondary-btn dash-project-row-action dash-open-content-action" type="button" onClick={() => setOpenId(c.id)}><IconDatabase size={14} /> <span className="dash-project-row-action-label">{t('cms.showContentShort')}</span></button>
                <div className={`dash-project-row-menu-wrap${menuOpenId === c.id ? ' is-open' : ''}`}>
                  <button
                    className="project-menu-btn dash-icon-btn dash-page-options-btn"
                    type="button"
                    aria-label={t('cms.moreOptions')}
                    data-tooltip={t('cms.options')}
                    onClick={(event) => toggleRowMenu(event, c.id, 234)}
                  >
                    <IconDots size={16} />
                  </button>
                  {menuOpenId === c.id ? createPortal(
                    <div className={`project-menu dash-project-row-menu dash-cms-row-menu-portal${listMenu.up ? ' is-up' : ''}`} style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}>
                      <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); setOpenId(c.id) }}>
                        <IconDatabase size={14} /> {t('cms.showContent')}
                      </button>
                      <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpenCms({ collectionId: c.id }) }}>
                        <IconPencil size={14} /> {t('cms.openInEditor')}
                      </button>
                      {collectionUrlFor?.(c.id) ? (
                        <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); window.open(collectionUrlFor(c.id)!, '_blank', 'noreferrer') }}>
                          <IconExternalLink size={14} /> {t('cms.viewPublished')}
                        </button>
                      ) : null}
                      {collectionUrlFor?.(c.id) ? (
                        <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); void navigator.clipboard?.writeText(collectionUrlFor(c.id)!); showToast(t('cms.linkCopied')) }}>
                          <IconLink size={14} /> {t('cms.copyLink')}
                        </button>
                      ) : null}
                      {onDeleteCollection ? (
                        <button
                          className="project-menu-item danger"
                          type="button"
                          onClick={() => {
                            setMenuOpenId(null)
                            setCollectionToDelete(c)
                          }}
                        >
                          <IconTrash size={14} /> {t('cms.deleteCollection')}
                        </button>
                      ) : null}
                    </div>,
                    document.body,
                  ) : null}
                </div>
              </div>
            </article>
            )
          })}
          </div>
        </div>
        </div>
        {/* Y al pie, donde se termina de leer. */}
        <Pagination page={pagedCollections.page} totalPages={pagedCollections.totalPages} total={pagedCollections.total} start={pagedCollections.start} end={pagedCollections.end} onPage={pagedCollections.setPage} unit={t('cms.unit')} alwaysShow />
        </>
      )}
    </div>
  )
}
